import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpService } from '../../shared/services/http.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DialogConfirmComponent } from '../../dialog-confirm/dialog-confirm.component';

@Component({
    selector: 'app-global-search-replace',
    templateUrl: './global-search-replace.component.html',
    styleUrls: ['./global-search-replace.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GlobalSearchReplaceComponent implements OnInit {

  searchTerm: string = '';
  replaceTerm: string = '';
  wholeWord: boolean = true;
  ignoreCase: boolean = false;

  displayedColumns = ['select', 'name', 'section', 'type', 'value'];
  dataSource: MatTableDataSource<any>;
  selection = new SelectionModel<any>(true, []);

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  results: any[] = [];
  loading = false;
  private lastSearchTerm = '';
  private lastWholeWord: boolean;
  private lastIgnoreCase: boolean;

  filterSection = '';
  sectionOptions: string[] = [];
  filterName = '';

  applyNameFilter() {
    this.dataSource.filter = this.filterName.trim().toLowerCase();
    this.updateUrl();
  }

  skeletonRows: number[][] = [];

  generateSkeletonRows() {
    this.skeletonRows = Array(10).fill(0).map(() => {
      // Random number of bars between 5 and 8 to ensure density
      const barCount = Math.floor(Math.random() * (8 - 5 + 1)) + 5;
      const bars = [];
      for (let i = 0; i < barCount; i++) {
        // Random flex weight between 1 and 10 to give variety in width
        bars.push(Math.floor(Math.random() * 10) + 1);
      }
      return bars;
    });
  }

  constructor(
    private httpService: HttpService,
    private location: Location,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal
  ) { }

  ngOnInit() {
    this.dataSource = new MatTableDataSource([]);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = (row: any, filter: string) =>
      (row.name || '').toLowerCase().includes(filter);

    this.sectionOptions = [
      '/actions', '/complete', '/expand', '/feedback', '/intro',
      '/link', '/podcast', '/quiz', '/rating/audio', '/rating/results',
      '/rating/video', '/read', '/references', '/reflection', '/reflections',
      '/reminders', '/research', '/send-reflections', '/sometime', '/sometimes',
      '/video', '/welcome'
    ];

    // Subscribe to query params to trigger search
    this.route.queryParams.subscribe(params => {
      // Always sync params
      if (params['replace']) {
        this.replaceTerm = params['replace'];
      }
      if (params['wholeWord'] !== undefined) {
        this.wholeWord = params['wholeWord'] === 'true';
      }
      if (params['ignoreCase'] !== undefined) {
        this.ignoreCase = params['ignoreCase'] === 'true';
      }
      if (params['filterName'] !== undefined) {
        this.filterName = params['filterName'];
        this.applyNameFilter();
      }
      if (params['filterSection'] !== undefined) {
        this.filterSection = params['filterSection'];
      }

      const term = params['term'];
      if (term) {
        this.searchTerm = term;

        const optionsChanged = this.wholeWord !== this.lastWholeWord || this.ignoreCase !== this.lastIgnoreCase;

        // Only search if term or options changed, or if it's a fresh load
        if (this.searchTerm !== this.lastSearchTerm || optionsChanged || this.results.length === 0) {
          this._performSearch();
        }
      } else {
        // Clear results if no term
        this.results = [];
        this.dataSource.data = [];
        this.lastSearchTerm = '';
      }
    });
  }

  getHighlightedText(text: string, isSelected: boolean): SafeHtml {
    if (!this.searchTerm || !text) {
      return text;
    }
    // Escape special characters for regex
    const escapedTerm = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Construct regex based on wholeWord flag
    const pattern = this.wholeWord ? `\\b(${escapedTerm})\\b` : `(${escapedTerm})`;
    // Add 'i' flag if ignoreCase is true
    const flags = this.ignoreCase ? 'gi' : 'g';
    const re = new RegExp(pattern, flags);

    let replacement = '<span class="highlight">$1</span>';

    if (this.replaceTerm && isSelected) {
      // If there is a replace term AND the row is selected, show the match crossed out, followed by the replacement
      replacement = '<span class="highlight strikethrough">$1</span> <span class="replacement">' + this.replaceTerm + '</span>';
    }

    const highlighted = text.replace(re, replacement);
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  updateUrl() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        term: this.searchTerm || null,
        replace: this.replaceTerm || null,
        wholeWord: this.wholeWord ? 'true' : null,
        ignoreCase: this.ignoreCase,
        filterName: this.filterName || null,
        filterSection: this.filterSection || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  search() {
    this.updateUrl();
  }

  applyFilter() {
    this.updateUrl();
    this._performSearch();
  }

  async _performSearch() {
    if (!this.searchTerm.trim()) return;

    this.loading = true;
    this.generateSkeletonRows();

    // Clear data immediately to show skeleton
    this.dataSource.data = [];

    try {
      // Wait for both the API call and at least 500ms
      const [res]: [any, any] = await Promise.all([
        this.httpService.postAsync('hcontent/globalSearchAndReplace', {
          term: this.searchTerm,
          wholeWord: this.wholeWord,
          ignoreCase: this.ignoreCase,
          ...(this.filterSection && { filterSection: this.filterSection })
        }),
        new Promise(resolve => setTimeout(resolve, 500))
      ]);

      this.results = res.data || [];
      if (this.results.length > 0) {
        this.sectionOptions = [...new Set(this.results.map(r => r.section).filter(Boolean))].sort() as string[];
      }

      this.lastSearchTerm = this.searchTerm;
      this.lastWholeWord = this.wholeWord;
      this.lastIgnoreCase = this.ignoreCase;

      this.dataSource.data = this.results;
      this.dataSource.paginator = this.paginator;
      this.selection.clear();
    } catch (e) {
      console.error('Search failed', e);
      alert('Search failed');
    } finally {
      this.loading = false;
    }
  }

  async openConfirmDialog(message: string): Promise<boolean> {
    try {
      const modalRef = this.modalService.open(DialogConfirmComponent, {
        windowClass: 'center-modal-window'
      });
      modalRef.componentInstance.message = message;
      return await modalRef.result;
    } catch (e) {
      return false;
    }
  }

  async replace() {
    // if (!this.replaceTerm) return;

    const selected = this.selection.selected;
    if (selected.length === 0) {
      alert('Please select items to replace');
      return;
    }

    const confirmed = await this.openConfirmDialog(`Are you sure you want to replace "${this.searchTerm}" with "${this.replaceTerm}" in ${selected.length} items?`);

    if (!confirmed) {
      return;
    }

    this.loading = true;
    this.generateSkeletonRows();

    try {
      const ids = selected.map(item => item.id);

      // Wait for both the API call and at least 500ms
      const [res]: [any, any] = await Promise.all([
        this.httpService.postAsync('hcontent/globalSearchAndReplace', {
          term: this.searchTerm,
          replace: this.replaceTerm,
          replaceIds: ids,
          wholeWord: this.wholeWord,
          ignoreCase: this.ignoreCase
        }),
        new Promise(resolve => setTimeout(resolve, 500))
      ]);

      if (res.success) {

        // Search for the replaced term
        this.searchTerm = this.replaceTerm;
        // Clear the replacement input
        this.replaceTerm = '';

        this._performSearch();

        this.updateUrl(); // Trigger search and update URL
      } else {
        alert('Replacement failed: ' + res.message);
      }
    } catch (e) {
      console.error('Replace failed', e);
      alert('Replace failed');
    } finally {
      this.loading = false;
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
        this.selection.clear() :
        this.dataSource.data.forEach(row => this.selection.select(row));
  }

}
